import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

interface Props {
  daysSinceLastChange?: number | null
  /** Whether to show this reminder (from Admin settings) */
  enabled?: boolean
}

/**
 * Password Change Reminder Banner
 * Shows if it's been more than 90 days since last password change
 * Can be dismissed for 10 days
 * Only shows if enabled in Admin settings
 */
export default function PasswordReminderBanner({ daysSinceLastChange, enabled = true }: Props) {
  const [dismissed, setDismissed] = useState(false)

  // Check localStorage for dismissal (10 days)
  useEffect(() => {
    if (daysSinceLastChange == null) return
    const dismissKey = `password_reminder_dismissed`
    const dismissedUntil = localStorage.getItem(dismissKey)
    if (dismissedUntil) {
      const dismissDate = new Date(dismissedUntil)
      if (dismissDate > new Date()) {
        setDismissed(true)
      } else {
        localStorage.removeItem(dismissKey)
      }
    }
  }, [daysSinceLastChange])

  const handleDismiss = () => {
    setDismissed(true)
    // Dismiss for 10 days
    const dismissUntil = new Date()
    dismissUntil.setDate(dismissUntil.getDate() + 10)
    localStorage.setItem('password_reminder_dismissed', dismissUntil.toISOString())
  }

  if (!enabled) return null
  if (daysSinceLastChange == null) return null // Null means password never changed, don't show reminder
  if (daysSinceLastChange < 90) return null // Less than 90 days, don't show
  if (dismissed) return null

  return (
    <motion.div
      className="mt-6 p-4 rounded-xl border border-orange-200 bg-orange-50 text-orange-800"
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="font-semibold mb-1">
            Password Change Reminder
          </p>
          <p className="text-sm">
            It has been <strong>{daysSinceLastChange} days</strong> since your last password change. 
            Please consider updating your password in{' '}
            <Link to="/settings" className="underline font-medium hover:text-orange-900">
              Settings & Details
            </Link>
            {' '}for better security.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-sm underline hover:text-orange-900 focus:outline-none focus:ring-2 focus:ring-orange-400/60 rounded px-2"
          title="Dismiss for 10 days"
        >
          Dismiss
        </button>
      </div>
    </motion.div>
  )
}

